import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { InventoryEntryModel } from 'src/app/models/inventory/inventory-entry.model';
import { SubrentalEntryPrintModel } from 'src/app/models/print/subrental/subrental-entry-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SubrentalEntryTableModel } from 'src/app/models/tables/subrental/subrental-entry-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SubrentalEntryService {

  constructor(private _httpClient: HttpClient) { }

  createSubrentalEntry(subrentalEntryContract: InventoryEntryModel): Observable<InventoryEntryModel> {
    return this._httpClient.post<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.CreateSubrentalEntry}`,
      subrentalEntryContract);
  }

  deleteSubrentalEntry(subrentalEntryId: number): Observable<InventoryEntryModel> {
    return this._httpClient.delete<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.DeleteSubrentalEntry}/${subrentalEntryId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams()
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetColumnsForGeneralConsultation}`);
  }

  getEmptySubrentalEntryPrint(): Observable<SubrentalEntryPrintModel> {
    return this._httpClient.get<SubrentalEntryPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetEmptySubrentalEntryPrint}`);
  }

  getFirstSubrentalEntryByKeysAsync(branchId: number): Observable<InventoryEntryModel> {
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetFirstSubrentalEntryByKeys}/${branchId}`);
  }

  getLastSubrentalEntryByCompanyIdAsync(itemFilters: Array<ItemFilterModel>): Observable<InventoryEntryModel> {
    return this._httpClient.post<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetLastSubrentalEntryByCompanyId}`,
      itemFilters);
  }

  getLastSubrentalEntryByKeysAsync(branchId: number): Observable<InventoryEntryModel> {
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetLastSubrentalEntryByKeys}/${branchId}`);
  }

  getNextSubrentalEntryByKeysAsync(branchId: number, consecutive: number | null): Observable<InventoryEntryModel> {
    let params = new HttpParams().set('branchId', branchId)
    if (consecutive !== null)
      params = params.set('consecutive', consecutive);
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetNextSubrentalEntryByKeys}`,
      { params: params });
  }

  getPreviusSubrentalEntryByKeysAsync(branchId: number, consecutive: number | null): Observable<InventoryEntryModel> {
    let params = new HttpParams().set('branchId', branchId)
    if (consecutive !== null)
      params = params.set('consecutive', consecutive);
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetPreviusSubrentalEntryByKeys}`,
      { params: params });
  }

  getSubrentalEntriesForGeneralConsultation(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<SubrentalEntryTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<SubrentalEntryTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetSubrentalEntriesForGeneralConsultation}`,
      filters, { params: params }
    );
  }

  getSubrentalEntryById(subrentalEntryId: number): Observable<InventoryEntryModel> {
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetSubrentalEntryById}/${subrentalEntryId}`);
  }

  getSubrentalEntryByKeysAsync(branchId: number, consecutive: number): Observable<InventoryEntryModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('consecutive', consecutive);
    return this._httpClient.get<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetSubrentalEntryByKeys}`,
      { params: params });
  }

  getSubrentalEntryByKeysByPrint(branchId: number, consecutive: number, subrentalEntryId: number): Observable<SubrentalEntryPrintModel> {
    const params = new HttpParams()
      .set('branchId', branchId)
      .set('consecutive', consecutive)
      .set('subrentalEntryId', subrentalEntryId)
    return this._httpClient.get<SubrentalEntryPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetSubrentalEntryPrintByKeys}`,
      { params: params });
  }

  getSubrentalEntryByKeysForFormats(itemFilters: Array<ItemFilterModel>): Observable<InventoryEntryModel> {
    return this._httpClient.post<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.GetSubrentalEntryByKeysForFormats}`,
      itemFilters);
  }

  setSubrentalEntryState(subrentalEntryId: number): Observable<InventoryEntryModel> {
    return this._httpClient.put<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.SetSubrentalEntryState}/${subrentalEntryId}`,
      null);
  }

  updateSubrentalEntry(subrentalEntryContract: InventoryEntryModel): Observable<InventoryEntryModel> {
    return this._httpClient.put<InventoryEntryModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.subrentalEntry.UpdateSubrentalEntry}`,
      subrentalEntryContract);
  }
}


